import type { HttpClient } from './http';

export interface WorkflowAgentRunEvent {
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  createdAt: string;
  runId?: string;
}

export interface WorkflowAgentRun {
  id: string;
  userId: string;
  workflowId: number;
  goal: string;
  parentRunId: string | null;
  status:
    | 'queued'
    | 'running'
    | 'waiting_approval'
    | 'completed'
    | 'failed'
    | 'cancelled'
    | 'rejected';
  stage: string;
  failureReason: string | null;
  planSummary: string | null;
  planOperations: unknown[] | null;
  createdAt: string;
  updatedAt: string;
  events: WorkflowAgentRunEvent[];
}

export interface WorkflowAgentRunSummary {
  id: string;
  workflowId: number;
  goal: string;
  parentRunId: string | null;
  status: WorkflowAgentRun['status'];
  stage: string;
  planSummary: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export type WorkflowAgentStreamHandlers = {
  onEvent: (event: WorkflowAgentRunEvent) => void;
  onError?: (error: unknown) => void;
  onClose?: () => void;
  signal?: AbortSignal;
};

export interface WorkflowAgentApi {
  listRuns(
    workflowId: number,
    options?: { limit?: number; cursor?: string | null }
  ): Promise<{
    runs: WorkflowAgentRunSummary[];
    nextCursor: string | null;
  }>;
  createRun(payload: {
    workflowId: number;
    goal: string;
    parentRunId?: string | null;
  }): Promise<{ runId: string; status: string }>;
  getRun(runId: string): Promise<WorkflowAgentRun>;
  getThread(runId: string): Promise<WorkflowAgentRun[]>;
  approve(
    runId: string,
    options?: { autoApproveAll?: boolean }
  ): Promise<WorkflowAgentRun>;
  reject(runId: string, reason?: string): Promise<WorkflowAgentRun>;
  cancel(runId: string): Promise<WorkflowAgentRun>;
  deleteRun(runId: string): Promise<void>;
  streamRun(
    runId: string,
    handlers: WorkflowAgentStreamHandlers & { afterSeq?: number }
  ): Promise<void>;
}

async function consumeSseStream(
  response: Response,
  handlers: WorkflowAgentStreamHandlers
): Promise<void> {
  if (!response.body) {
    handlers.onClose?.();
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIndex = buffer.indexOf('\n\n');
      // SSE messages are separated by a blank line.
      while (sepIndex !== -1) {
        const raw = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);
        const dataLine = raw
          .split('\n')
          .find((line) => line.startsWith('data:'));
        if (dataLine) {
          const json = dataLine.slice(5).trim();
          if (json) {
            try {
              const event = JSON.parse(json) as WorkflowAgentRunEvent;
              handlers.onEvent(event);
            } catch (err) {
              handlers.onError?.(err);
            }
          }
        }
        sepIndex = buffer.indexOf('\n\n');
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name !== 'AbortError') {
      handlers.onError?.(err);
    }
  } finally {
    handlers.onClose?.();
  }
}

export function createWorkflowAgentApi(
  http: HttpClient,
  baseUrl?: string
): WorkflowAgentApi {
  return {
    listRuns: (workflowId, options) => {
      const params = new URLSearchParams();
      params.set('workflowId', String(workflowId));
      if (options?.limit !== undefined)
        params.set('limit', String(options.limit));
      if (options?.cursor) params.set('cursor', options.cursor);
      return http.get<{
        runs: WorkflowAgentRunSummary[];
        nextCursor: string | null;
      }>(`/workflow-agent/runs?${params.toString()}`);
    },
    createRun: (payload) =>
      http.post<{ runId: string; status: string }>(
        '/workflow-agent/runs',
        payload
      ),
    getRun: (runId) =>
      http.get<WorkflowAgentRun>(`/workflow-agent/runs/${runId}`),
    getThread: (runId) =>
      http.get<WorkflowAgentRun[]>(`/workflow-agent/runs/${runId}/thread`),
    approve: (runId, options) =>
      http.post<WorkflowAgentRun>(
        `/workflow-agent/runs/${runId}/approve`,
        options ?? {}
      ),
    reject: (runId, reason) =>
      http.post<WorkflowAgentRun>(`/workflow-agent/runs/${runId}/reject`, {
        reason,
      }),
    cancel: (runId) =>
      http.post<WorkflowAgentRun>(`/workflow-agent/runs/${runId}/cancel`, {}),
    deleteRun: (runId) => http.delete<void>(`/workflow-agent/runs/${runId}`),
    streamRun: async (runId, handlers) => {
      const params = new URLSearchParams();
      if (handlers.afterSeq && handlers.afterSeq > 0) {
        params.set('afterSeq', String(handlers.afterSeq));
      }
      const qs = params.toString();
      const url = `${baseUrl ?? ''}/workflow-agent/runs/${runId}/stream${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'text/event-stream' },
        signal: handlers.signal,
      });
      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status}`);
      }
      await consumeSseStream(response, handlers);
    },
  };
}
